import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { BusinessMessagesProvider, useBusinessMessages } from "../../context/BusinessMessagesContext";
import businessOwnerTheme, { brand } from "./theme";
import DirectionalThemeProvider from "../DelivryCompany/DirectionalThemeProvider";
import sidebarConfig from "./sidebarConfig";

const DRAWER_WIDTH = 260;
const MINI_DRAWER_WIDTH = 76;

export default function BusinessOwnerLayout() {
  return (
    <BusinessMessagesProvider>
      <BusinessOwnerLayoutContent />
    </BusinessMessagesProvider>
  );
}

function BusinessOwnerLayoutContent() {
  const { t } = useTranslation(["boLayout", "boSidebar", "common"]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { user, logout } = useAuth();
  const { unreadCount } = useBusinessMessages();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery(businessOwnerTheme.breakpoints.up("md"));

  function handleMenuClick() {
    if (isMdUp) {
      setCollapsed((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  }

  function handleLogout() {
    setMenuAnchor(null);
    logout();
    navigate("/business-owner/login");
  }

  function renderDrawerContent(isCollapsed) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Toolbar
          sx={{
            gap: 1.5,
            px: isCollapsed ? 1 : 2.5,
            justifyContent: isCollapsed ? "center" : "flex-start",
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              background: `linear-gradient(135deg, ${brand.emeraldLight}, ${brand.emeraldDeep})`,
              width: 36,
              height: 36,
              borderRadius: 2.5,
              boxShadow: `0 4px 14px ${brand.emeraldDeep}66`,
            }}
          >
            <StorefrontIcon fontSize="small" />
          </Avatar>
          {!isCollapsed && (
            <Box sx={{ overflow: "hidden" }}>
              <Typography variant="subtitle1" noWrap fontWeight={800} sx={{ color: "#fff", lineHeight: 1.2 }}>
                Salatty
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: brand.amberLight, fontWeight: 600 }}>
                {t("boLayout:businessConsole")}
              </Typography>
            </Box>
          )}
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <List sx={{ px: isCollapsed ? 1 : 1.5, py: 2, flexGrow: 1 }}>
          {sidebarConfig.map(({ labelKey, path, icon: Icon, showUnreadBadge }) => {
            const label = t(`boSidebar:${labelKey}`);
            const button = (
              <ListItemButton
                key={path}
                component={NavLink}
                to={path}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  px: isCollapsed ? 1.25 : 2,
                  borderLeft: "3px solid transparent",
                  color: "rgba(255,255,255,0.72)",
                  "&.active": {
                    bgcolor: "rgba(18, 184, 134, 0.22)",
                    borderLeft: `3px solid ${brand.amberLight}`,
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: brand.amberLight },
                  },
                  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                  transition: "background-color 0.15s ease, border-color 0.15s ease",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? 0 : 40,
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  <Badge
                    color="error"
                    variant={isCollapsed ? "dot" : "standard"}
                    badgeContent={unreadCount}
                    invisible={!showUnreadBadge || !unreadCount}
                  >
                    <Icon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}>
                    {label}
                  </ListItemText>
                )}
              </ListItemButton>
            );

            return isCollapsed ? (
              <Tooltip key={path} title={label} placement="right" arrow>
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}
        </List>
      </Box>
    );
  }

  const desktopWidth = collapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <DirectionalThemeProvider theme={businessOwnerTheme}>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <AppBar
          position="fixed"
          color="default"
          sx={{
            width: { md: `calc(100% - ${desktopWidth}px)` },
            ml: { md: `${desktopWidth}px` },
            bgcolor: "background.paper",
            transition: (theme) =>
              theme.transitions.create(["width", "margin"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <IconButton color="inherit" edge="start" onClick={handleMenuClick}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ gap: 1, borderRadius: 2 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${brand.emeraldLight}, ${brand.emeraldDeep})`,
                }}
              >
                {(user?.fullName || "B").charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem disabled sx={{ opacity: "1 !important" }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.fullName || t("boLayout:businessOwnerFallback")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t("common:logOut")}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { md: desktopWidth }, flexShrink: { md: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                background: `linear-gradient(165deg, ${brand.nightGreen}, ${brand.tealEnd})`,
                color: "#fff",
              },
            }}
          >
            {renderDrawerContent(false)}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              width: desktopWidth,
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
              "& .MuiDrawer-paper": {
                width: desktopWidth,
                boxSizing: "border-box",
                background: `linear-gradient(165deg, ${brand.nightGreen}, ${brand.tealEnd})`,
                color: "#fff",
                border: "none",
                overflowX: "hidden",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.standard,
                  }),
              },
            }}
            open
          >
            {renderDrawerContent(collapsed)}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${desktopWidth}px)` },
            bgcolor: "background.default",
            minHeight: "100vh",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Toolbar />
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </DirectionalThemeProvider>
  );
}
